import { AxiosRequestConfig } from "axios";
import { SWRConfiguration } from "swr";
import Bugsnag from "@bugsnag/js";
import { Auth } from "@aws-amplify/auth";
import axiosClient, { config as axiosClientConfigBase } from "./axiosClient";
import getCognitoToken from "../services/getCognitoToken/getCognitoToken";
import { keysToCamelCase } from "../services/convert";

export async function fetcher(url: string) {
  const auth = await Auth.currentAuthenticatedUser();

  const token = getCognitoToken(auth);

  const config: AxiosRequestConfig = {
    ...axiosClientConfigBase,
    headers: { Authorization: `Bearer ${token}` },
  };

  const res = await axiosClient.get(url, config);

  return keysToCamelCase(res.data);
}

const UUID_REGEX =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export function normalizeBugsnagKey(key: string): string {
  return key
    .split("?")[0]
    .replace(UUID_REGEX, ":id")
    .replace(/\/\d+(?=\/|$)/g, "/:id");
}

export function shouldReportApiError(error: any): boolean {
  // Amplify rejects with a bare string when the Cognito session is gone.
  const message = typeof error === "string" ? error : error?.message;
  if (message === "The user is not authenticated") return false;

  // The backend rejected the token: an ended session, not a front-end bug.
  if (error?.response?.status === 401) return false;

  // Transient network conditions, not front-end bugs. 5xx are intentionally
  // still reported (normalizeBugsnagKey collapses them into one group per
  // route) so a real backend regression stays visible.
  const code = error?.code;
  if (
    code === "ERR_NETWORK" ||
    code === "ECONNABORTED" ||
    code === "ERR_CANCELED" ||
    message === "Network Error"
  ) {
    return false;
  }

  return true;
}

/**
 * Key is used to group the error in Bugsnag. If the url is not dynamic, you can just pass they key useSWR gives you.
 * If the URL is dynamic, pass a custom key.
 * @param error
 * @param key
 */
export function reportApiErrorToBugsnag(error: any, key: string) {
  if (!shouldReportApiError(error)) return;

  Bugsnag.notify(error, (event) => {
    const status = error.response?.status;
    const normalizedKey = normalizeBugsnagKey(key);

    event.groupingHash = `${normalizedKey}-${status}`;
    event.context = normalizedKey;
  });
}

export function dontReportApiErrorToBugsnag(error: any, key: string) {
  // This method doesn't do anything but passing `undefined` is not allowed and creating an empty method every time is boring.
  // This empty method allows us to make reporting to bugsnag an opt-out feature instead of opt-in so we can't forget.
}

const swrConfig: SWRConfiguration = {
  fetcher,
  onError: reportApiErrorToBugsnag,
};

export default swrConfig;
